import React from 'react';
import { createRoot } from 'react-dom/client';

import 'highlight.js/styles/shades-of-purple.css';
import './style.css';

import App from './App';
import { loadDeepZoom } from './loadDeepZoom';
import { FRACTAL_DPR_CHANGE_EVENT } from './ResponsiveFractalCanvas';

// The browser harness exercises the actual production chunks and workers.
// Merely enabling the hook must not fetch the lazy engine.
if (new URLSearchParams(window.location.search).get('deepZoomTest') === '1') {
  window.__deepZoomTest = {
    load: loadDeepZoom,
    getCanvases: () => Array.from(document.querySelectorAll('canvas'))
      .filter(canvas => typeof canvas.__setFractalCamera === 'function'),
  };
}

const VIZ_POLL_INTERVAL_MS = 200;
const RENDER_EVENT = new CustomEvent('render');
let previousDevicePixelRatio = window.devicePixelRatio;

function observeFractalCanvases(rootElement) {
  if (typeof IntersectionObserver === 'undefined') return;
  const observedCanvases = new WeakSet();
  const visibleCanvases = new Set();
  const pendingCanvases = new Set();
  const MAX_RENDERS_PER_FRAME = 4;
  let scheduledFrame = null;

  const flushPending = () => {
    scheduledFrame = null;
    let renderedCount = 0;
    for (const canvas of pendingCanvases) {
      pendingCanvases.delete(canvas);
      if (visibleCanvases.has(canvas) && canvas.isConnected) {
        canvas.dispatchEvent(RENDER_EVENT);
        if (++renderedCount >= MAX_RENDERS_PER_FRAME && pendingCanvases.size > 0) {
          scheduledFrame = requestAnimationFrame(flushPending);
          break;
        }
      }
    }
  };

  const intersectionObserver = new IntersectionObserver((entries) => {
    let addedVisible = false;
    for (const entry of entries) {
      const canvas = entry.target;
      if (entry.isIntersecting) {
        visibleCanvases.add(canvas);
        pendingCanvases.add(canvas);
        addedVisible = true;
      } else {
        visibleCanvases.delete(canvas);
        pendingCanvases.delete(canvas);
      }
    }
    if (addedVisible && scheduledFrame === null) {
      flushPending();
    }
  }, { rootMargin: '200px 0px' });

  const registerCanvas = (canvas) => {
    if (!observedCanvases.has(canvas)) {
      observedCanvases.add(canvas);
      intersectionObserver.observe(canvas);
      const rect = canvas.getBoundingClientRect();
      if ((rect.width > 0 || rect.height > 0) &&
          rect.bottom >= -200 &&
          rect.top <= (window.innerHeight || 0) + 200) {
        visibleCanvases.add(canvas);
        pendingCanvases.add(canvas);
        if (scheduledFrame === null) {
          flushPending();
        }
      }
    }
  };

  const unregisterCanvas = (canvas) => {
    observedCanvases.delete(canvas);
    visibleCanvases.delete(canvas);
    pendingCanvases.delete(canvas);
    intersectionObserver.unobserve?.(canvas);
  };

  const forEachCanvas = (node, callback) => {
    if (!node || node.nodeType !== 1) return;
    if (node.tagName === 'CANVAS') {
      callback(node);
      return;
    }
    const canvases = node.getElementsByTagName?.('canvas');
    if (!canvases) return;
    for (const canvas of canvases) {
      callback(canvas);
    }
  };

  forEachCanvas(rootElement || document.body, registerCanvas);
  if (typeof MutationObserver !== 'undefined') {
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const added of mutation.addedNodes) {
          forEachCanvas(added, registerCanvas);
        }
        for (const removed of mutation.removedNodes) {
          forEachCanvas(removed, unregisterCanvas);
        }
      }
    });
    mutationObserver.observe(rootElement || document.body, {
      childList: true,
      subtree: true,
    });
  }
}

function renderVisibleFractalCanvases() {
  // A change in DPR alone may not fire a resize or media query event.
  // The existing viewport poll can notify responsive canvases without another timer.
  if (!Object.is(window.devicePixelRatio, previousDevicePixelRatio)) {
    previousDevicePixelRatio = window.devicePixelRatio;
    window.dispatchEvent(new Event(FRACTAL_DPR_CHANGE_EVENT));
  }
  if (typeof IntersectionObserver !== 'undefined') return;
  const viewportHeight = window.innerHeight;
  const canvases = document.getElementsByTagName('canvas');
  [...canvases].forEach((canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      // canvas is not visible
    } else {
      // canvas is visible
      canvas.dispatchEvent(RENDER_EVENT);
    }
  });
}

function renderVisibleFractalCanvasesForever() {
  renderVisibleFractalCanvases();
  setTimeout(() => {
    renderVisibleFractalCanvasesForever();
  }, VIZ_POLL_INTERVAL_MS);
}

// entry point (React 18+ createRoot API)
const container = document.getElementById('react-mount');
createRoot(container).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
observeFractalCanvases(container);
renderVisibleFractalCanvasesForever();
